import { Box, Typography } from '@mui/material';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import Button from 'packages/novous_web/src/common/components/Button';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import InputField from 'packages/novous_web/src/common/components/InputField';
import TimeField from 'packages/novous_web/src/common/components/TimeField';
import React, { useEffect, useState } from 'react';
import { setTime } from 'react-datepicker/dist/date_utils';

interface CompanyData {
    id: string;
    name: string;
    description: string;
    type: string;
    crn: string;
    address: {
        fullAddress: string;
        city: string;
        state: string;
        pincode: number;
        country: string;
    };
    contacts: {
        telephoneNo: string;
        mobileNo: string;
        email: string;
    };
    url: string;
    status: number;
    deviceTypes: string[] | null;
}

const UserSettings = () => {
    const [selectedCompany, setSelectedCompany] = useState<CompanyData | null>(null);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const { user } = useAuth();

    const {
        data: UserSettingDataData,
        error: UserSettingDataError,
        loading: UserSettingDataLoading,
        fetchData: UserSettingDataRequestData,
    } = useAxios<any>(API_ENDPOINTS.users.USER_SETTINGS_DATA, "GET");

    const {
        data: UserSettingFetchData,
        error: UserSettingFetchError,
        loading: UserSettingFetchLoading,
        fetchData: UserSettingFetchRequestData,
    } = useAxios<any>(API_ENDPOINTS.users.USER_SETTINGS_FETCH, "POST");

    useEffect(() => {
        UserSettingFetchRequestData({
            fleetId: user?.authorities === "ROLE_SUPER_ADMIN" ? user?.id : user?.customer_id
        });
    }, [])

    const [toggleStates, setToggleStates] = useState<{ [key: string]: any }>({});

    useEffect(() => {
        UserSettingDataRequestData();
    }, []);

    const [fieldEditability, setFieldEditability] = useState<{ [key: string]: boolean }>({});
    useEffect(() => {
        const allFeatures = [
            { key: "BLUR", data: UserSettingDataData?.data?.BLUR ?? { name: "blur", fields: [] }, userData: UserSettingFetchData?.data?.blur },
            { key: "CABIN_OCCUPANCY", data: UserSettingDataData?.data?.CABIN_OCCUPANCY ?? { name: "cabin_occupancy", fields: [] }, userData: UserSettingFetchData?.data?.cabinOccupancy },
            { key: "CONTINUOUS_DRIVING", data: UserSettingDataData?.data?.CONTINUOUS_DRIVING ?? { name: "continuous_driving", fields: [] }, userData: UserSettingFetchData?.data?.continuousDriving },
            { key: "DRIVER_IGNITION_BASED_RECOGNITION", data: UserSettingDataData?.data?.DRIVER_IGNITION_BASED_RECOGNITION ?? { name: "driver_ignition_based_recognition", fields: [] }, userData: UserSettingFetchData?.data?.driverIgnitionRecognition },
            { key: "NIGHT_DRIVING", data: UserSettingDataData?.data?.NIGHT_DRIVING ?? { name: "night_driving", fields: [] }, userData: UserSettingFetchData?.data?.nightDriving },
        ];

        const initialStates: { [key: string]: any } = {};
        const editAccessMap: { [key: string]: boolean } = {};

        allFeatures.forEach(({ data, userData }) => {
            if (!data) return;

            data.fields?.forEach((field: any) => {
                const key = `${data.name}.${field.name}`;
                let defaultValue;

                if (userData && field.name in userData) {
                    defaultValue = userData[field.name];
                } else {
                    defaultValue = field.type === "boolean"
                        ? false
                        : field.type === "Integer" || field.type === "long"
                            ? 0
                            : "";
                }

                initialStates[key] = defaultValue;

                // ✅ Super admin override
                const canEdit = user?.authorities === "ROLE_SUPER_ADMIN"
                    ? true // Super Admin can always edit
                    : userData?.authenticatedUSerEnabled === true;
                editAccessMap[key] = canEdit;
            });

            // ✅ Also apply the same logic for the enable toggle
            if (userData) {
                const toggleKey = `${data.name}.__enabled__`;
                initialStates[toggleKey] = user?.authorities === "ROLE_SUPER_ADMIN"
                    ? userData.authenticatedUSerEnabled === true
                    : userData.enableFlag ?? false;
                editAccessMap[toggleKey] = user?.authorities === "ROLE_SUPER_ADMIN"
                    ? true
                    : userData.authenticatedUSerEnabled === true;

            }
        });

        setToggleStates(initialStates);
        setFieldEditability(editAccessMap);
    }, [UserSettingDataData, UserSettingFetchData]);

    const handleToggle = (key: string) => {
        setToggleStates(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const formatFeatureName = (name: string) => {
        return name
            .toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const formatLabel = (label: string) => {
        if (!label) return "";
        const snakeToSpace = label.replace(/_/g, ' ');
        const formatted = snakeToSpace.replace(/([a-z])([A-Z])/g, '$1 $2');
        return formatted
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const {
        data: companyList,
        error: companyListError,
        loading: companyListLoading,
        fetchData: fetchCompanyListData,
    } = useAxios<any>(`${API_ENDPOINTS.users.COMPANY_LIST}`, "GET");

    const {
        data: companyListByCrn,
        error: companyListByCrnError,
        loading: companyListByCrnLoading,
        fetchData: fetchCompanyListByCrnData,
    } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_CRN}`, "POST");

    // Initialize data
    useEffect(() => {
        fetchCompanyListData();
    }, []);

    const handleCompanyChange = (value: string) => {
        const company = companyList?.data?.find((c: CompanyData) => c.name === value);
        setSelectedCompany(company || null);
        setSelectedUser(null);

        if (company) {
            fetchCompanyListByCrnData({
                userId: company.id,
                userCrn: company.crn
            });
        }
    };

    const handleUserChange = (value: string) => {
        const user = companyListByCrn?.data?.find((u: any) => u.fullName === value);
        setSelectedUser(user || null);
        UserSettingFetchRequestData({
            fleetId: user?.authorities === "ROLE_SUPER_ADMIN" ? user?.id : user?.customer_id
        });
    };

    const isAnyFieldModified = () => {
        if (!UserSettingFetchData?.data || !UserSettingDataData?.data) return false;

        const allFeatures = [
            { key: "BLUR", data: UserSettingDataData?.data?.BLUR ?? { name: "blur", fields: [] }, userData: UserSettingFetchData?.data?.blur },
            { key: "CABIN_OCCUPANCY", data: UserSettingDataData?.data?.CABIN_OCCUPANCY ?? { name: "cabin_occupancy", fields: [] }, userData: UserSettingFetchData?.data?.cabinOccupancy },
            { key: "CONTINUOUS_DRIVING", data: UserSettingDataData?.data?.CONTINUOUS_DRIVING ?? { name: "continuous_driving", fields: [] }, userData: UserSettingFetchData?.data?.continuousDriving },
            { key: "DRIVER_IGNITION_BASED_RECOGNITION", data: UserSettingDataData?.data?.DRIVER_IGNITION_BASED_RECOGNITION ?? { name: "driver_ignition_based_recognition", fields: [] }, userData: UserSettingFetchData?.data?.driverIgnitionRecognition },
            { key: "NIGHT_DRIVING", data: UserSettingDataData?.data?.NIGHT_DRIVING ?? { name: "night_driving", fields: [] }, userData: UserSettingFetchData?.data?.nightDriving },
        ];

        for (const { data, userData } of allFeatures) {
            if (!data || !userData) continue;

            // Check toggle __enabled__ key
            const toggleKey = `${data.name}.__enabled__`;
            if ((toggleStates[toggleKey] ?? false) !== (userData.enableFlag ?? false)) {
                return true;
            }

            // Check each field
            for (const field of data.fields) {
                const key = `${data.name}.${field.name}`;
                const currentValue = toggleStates[key];
                const originalValue = userData[field.name];

                if (field.type === 'boolean') {
                    // toggleStates stores boolean as boolean? Confirm if string casted anywhere
                    if (!!currentValue !== !!originalValue) {
                        return true;
                    }
                } else if (field.type === 'Integer' || field.type === 'long') {
                    if (Number(currentValue) !== Number(originalValue)) {
                        return true;
                    }
                } else {
                    // string comparison ignoring leading/trailing whitespace
                    if ((currentValue ?? '').toString().trim() !== (originalValue ?? '').toString().trim()) {
                        return true;
                    }
                }
            }
        }

        return false;
    };


    const isSaveDisabled =
        user.authorities === "ROLE_SUPER_ADMIN"
            ? !selectedCompany || !selectedUser 
            : !isAnyFieldModified();

    const {
        data: UserSettingDataSave,
        error: UserSettingDataSaveError,
        loading: UserSettingDataSaveLoading,
        fetchData: fetchUserSettingDataSaveData,
    } = useAxios<any>(`${API_ENDPOINTS.users.USER_SETTING_SAVE}`, "POST");

    const handleSave = () => {
        if (user.authorities === "ROLE_SUPER_ADMIN" && !selectedUser) return;

        const fleetId = user.authorities === "ROLE_SUPER_ADMIN" ? selectedUser?.id : user?.customer_id;

        // Create array of objects with key and feature object to preserve keys
        const features = [
            { key: "BLUR", feature: UserSettingDataData?.data?.BLUR },
            { key: "CABIN_OCCUPANCY", feature: UserSettingDataData?.data?.CABIN_OCCUPANCY },
            { key: "CONTINUOUS_DRIVING", feature: UserSettingDataData?.data?.CONTINUOUS_DRIVING },
            { key: "DRIVER_IGNITION_BASED_RECOGNITION", feature: UserSettingDataData?.data?.DRIVER_IGNITION_BASED_RECOGNITION },
            { key: "NIGHT_DRIVING", feature: UserSettingDataData?.data?.NIGHT_DRIVING },
        ];

        
        // Map and filter features to only enabled ones
        const featureList = features
            .filter(({ feature }) => feature) // Ensure feature exists
            .map(({ key, feature }) => {
                const featureFields: any = {};
                feature.fields.forEach((field: any) => {
                    const toggleKey = `${feature.name}.${field.name}`;
                    let value = toggleStates[toggleKey];

                    if (field.type === "boolean") {
                        featureFields[field.name] = value === true;
                    } else if (field.type === "Integer" || field.type === "long") {
                        featureFields[field.name] = Number(value);
                    } else {
                        featureFields[field.name] = value;
                    }
                });

                const toggleKey = `${feature.name}.__enabled__`;
                const toggleEnabled = toggleStates[toggleKey] ?? false;
                featureFields["enableFlag"] = toggleEnabled;

                if (user.authorities === "ROLE_SUPER_ADMIN") {
                    featureFields["authenticatedUSerEnabled"] = toggleEnabled;
                }

                return {
                    feature: featureFields,
                    featureType: key,
                    enableFlag: toggleEnabled,
                    originalUserData: UserSettingFetchData?.data?.[feature.name], // for comparison only
                };
            })
            .filter(({ feature, originalUserData }) => {
                if (!originalUserData) return true;

                const isSuperAdmin = user.authorities === "ROLE_SUPER_ADMIN";

                if (isSuperAdmin) {
                    // ✅ Only check if authenticatedUSerEnabled changed
                    return feature.authenticatedUSerEnabled !== originalUserData.authenticatedUSerEnabled;
                } else {
                    // ✅ For non-superadmin: check enableFlag and fields
                    if (feature.enableFlag !== originalUserData.enableFlag) return true;

                    for (const key in feature) {
                        if (["enableFlag"].includes(key)) continue;

                        const current = feature[key];
                        const original = originalUserData[key];

                        // string comparison
                        if (typeof current === "string" && typeof original === "string") {
                            if (current.trim() !== original.trim()) return true;
                        }
                        // numeric or boolean comparison
                        else if (current !== original) {
                            return true;
                        }
                    }

                    return false; // if no change
                }
            })
            // Remove originalUserData from the final payload before sending
            .map(({ feature, featureType, enableFlag }) => ({
                feature,
                featureType,
                enableFlag,
            }));





        // .filter(feature => feature.enableFlag); // only include enabled features

        const payload = {
            fleetId,
            featureList,
        };

        console.log("Saving payload:", payload);

        fetchUserSettingDataSaveData(payload);
        setSelectedCompany(null);
        setSelectedUser(null);
        setToggleStates({});

        setTimeout(() => {
            UserSettingFetchRequestData({
                fleetId: user?.authorities === "ROLE_SUPER_ADMIN" ? user?.id : user?.customer_id
            });
        }, 500)
    };



    const renderFeatureSection = (feature: any) => {
        if (!feature) return null;

        const toggleKey = `${feature.name}.__enabled__`;
        const isEnabled = toggleStates[toggleKey] ?? false;

        const canEditFeature = user?.authorities === "ROLE_SUPER_ADMIN"
            ? true
            : fieldEditability[toggleKey] ?? false;
        return (
            <div
                key={feature.name}
                className="flex flex-col gap-4 border border-[#F0EFF8] p-4 rounded-lg" style={{
                    opacity:
                        !canEditFeature && user.authorities !== "ROLE_SUPER_ADMIN"
                            ? 0.5
                            : canEditFeature && user.authorities !== "ROLE_SUPER_ADMIN"
                                ? 1
                                : undefined

                }}>
                <div className="flex justify-between items-center">
                    <Typography variant="h6" fontWeight={600} fontSize={18}>
                        {formatFeatureName(feature.name)}
                    </Typography>

                    <label className="relative inline-flex items-center cursor-pointer">
                        <Box
                            className="w-12 h-6 flex items-center rounded-full transition-colors duration-300 ease-in-out"
                            sx={(theme) => ({
                                backgroundColor: isEnabled ? theme.palette.primary.main : theme.palette.secondary.main
                            })}
                        >
                            <div
                                className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                            />
                        </Box>
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={isEnabled}
                            onChange={() => {
                                if (canEditFeature) {
                                    setToggleStates(prev => ({ ...prev, [toggleKey]: !prev[toggleKey] }));
                                }
                            }}
                            disabled={!canEditFeature}
                        />
                    </label>
                </div>

                {feature.fields.map((field: any, idx: number) => {
                    const key = `${feature.name}.${field.name}`;
                    const fieldValue = toggleStates[key] ?? "";
                    const isEditable = fieldEditability[key] ?? false;

                    return (
                        <div key={idx} className="flex gap-1 mt-2">
                            {(feature.name === 'night_driving' && (field.name === 'startTime' || field.name === 'endTime')) ? (
                                <TimeField
                                    label={formatLabel(field.label || field.name)}
                                    value={fieldValue}
                                    onChange={(val) => setToggleStates(prev => ({ ...prev, [key]: val }))}
                                    showSeconds={true}
                                    name={''}
                                    isDisabled={!isEditable}
                                />
                            ) : (
                                <InputField
                                    label={formatLabel(field.label || field.name)}
                                    type={field.type === 'Integer' || field.type === 'long' ? 'number' : 'text'}
                                    value={typeof fieldValue === 'boolean' ? String(fieldValue) : fieldValue}
                                    onChange={(e) =>
                                        setToggleStates(prev => ({
                                            ...prev,
                                            [key]: e.target.value
                                        }))
                                    }
                                    isDisabled={!isEditable || typeof fieldValue === 'boolean'}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const { BLUR, CABIN_OCCUPANCY, CONTINUOUS_DRIVING, DRIVER_IGNITION_BASED_RECOGNITION, NIGHT_DRIVING } = UserSettingDataData?.data || {};

    const initializeToggleStates = () => {
        const allFeatures = [
            { key: "BLUR", data: UserSettingDataData?.data?.BLUR ?? { name: "blur", fields: [] }, userData: UserSettingFetchData?.data?.blur },
            { key: "CABIN_OCCUPANCY", data: UserSettingDataData?.data?.CABIN_OCCUPANCY ?? { name: "cabin_occupancy", fields: [] }, userData: UserSettingFetchData?.data?.cabinOccupancy },
            { key: "CONTINUOUS_DRIVING", data: UserSettingDataData?.data?.CONTINUOUS_DRIVING ?? { name: "continuous_driving", fields: [] }, userData: UserSettingFetchData?.data?.continuousDriving },
            { key: "DRIVER_IGNITION_BASED_RECOGNITION", data: UserSettingDataData?.data?.DRIVER_IGNITION_BASED_RECOGNITION ?? { name: "driver_ignition_based_recognition", fields: [] }, userData: UserSettingFetchData?.data?.driverIgnitionRecognition },
            { key: "NIGHT_DRIVING", data: UserSettingDataData?.data?.NIGHT_DRIVING ?? { name: "night_driving", fields: [] }, userData: UserSettingFetchData?.data?.nightDriving },
        ];

        const initialStates: { [key: string]: any } = {};
        const editAccessMap: { [key: string]: boolean } = {};

        allFeatures.forEach(({ data, userData }) => {
            if (!data) return;

            data.fields?.forEach((field: any) => {
                const key = `${data.name}.${field.name}`;
                let defaultValue;

                if (userData && field.name in userData) {
                    defaultValue = userData[field.name];
                } else {
                    defaultValue = field.type === "boolean"
                        ? false
                        : field.type === "Integer" || field.type === "long"
                            ? 0
                            : "";
                }

                initialStates[key] = defaultValue;

                const canEdit = user?.authorities === "ROLE_SUPER_ADMIN"
                    ? true
                    : userData?.authenticatedUSerEnabled === true;
                editAccessMap[key] = canEdit;
            });

            if (userData) {
                const toggleKey = `${data.name}.__enabled__`;
                initialStates[toggleKey] = user?.authorities === "ROLE_SUPER_ADMIN"
                    ? userData.authenticatedUSerEnabled === true
                    : userData.enableFlag ?? false;
                editAccessMap[toggleKey] = user?.authorities === "ROLE_SUPER_ADMIN"
                    ? true
                    : userData.authenticatedUSerEnabled === true;
            }
        });

        setToggleStates(initialStates);
        setFieldEditability(editAccessMap);
    };

    useEffect(() => {
        initializeToggleStates();
    }, [UserSettingDataData, UserSettingFetchData]);

    const handleCancel = () => {
        initializeToggleStates();
        setSelectedCompany(null);
        setSelectedUser(null);
    };



    return (
        <>
            <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'
                sx={() => ({ marginBottom: '16px' })}
            >
                User Settings
            </Typography>
            {user?.authorities === 'ROLE_SUPER_ADMIN' &&
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                    <CustomDropDownInput
                        label="Company"
                        options={companyList?.data?.map((company: CompanyData) => ({
                            value: company.name,
                            label: company.name
                        })) || []}
                        value={selectedCompany?.name || ''}
                        onChange={handleCompanyChange}
                        placeholder="Select Company"
                        // isDisabled={!selectedApk}
                        showDropdownArrow={true}
                    />

                    <CustomDropDownInput
                        label={selectedCompany ? "User" : "Select company first"}
                        options={companyListByCrn?.data?.map((user: any) => ({
                            value: user.fullName,
                            label: user.fullName
                        })) || []}
                        value={selectedUser?.fullName || ''}
                        onChange={handleUserChange}
                        placeholder={selectedCompany ? "Select User" : "Select company first"}
                        isDisabled={!selectedCompany}
                        showDropdownArrow={true}
                    />
                </div>
            }
            <div className='w-full grid grid-cols-1 md:grid-cols-2 gap-4'
                style={{
                    pointerEvents:
                        user?.authorities === 'ROLE_SUPER_ADMIN'
                            ? selectedCompany && selectedUser
                                ? 'auto'
                                : 'none'
                            : 'auto',
                    opacity:
                        user?.authorities === 'ROLE_SUPER_ADMIN'
                            ? selectedCompany && selectedUser
                                ? 1
                                : 0.5
                            : 1,
                }}

            >
                {renderFeatureSection(BLUR)}
                {renderFeatureSection(CABIN_OCCUPANCY)}
                {renderFeatureSection(CONTINUOUS_DRIVING)}
                {renderFeatureSection(DRIVER_IGNITION_BASED_RECOGNITION)}
                {renderFeatureSection(NIGHT_DRIVING)}
            </div>
            <div className="flex gap-2 justify-end mt-8">
                <CustomIconButton
                    text='Cancel'
                    background="transparent" outline={true} padding="0px"
                    variant="outlined"
                    onClick={handleCancel}
                />

                <Button
                    text='Save'
                    onClick={handleSave}
                    fullWidth={false}
                    disabled={isSaveDisabled}
                />
            </div>
        </>
    );
};

export default UserSettings;
